import { AfterViewChecked, AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Document, DocumentAction, FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS, FirstToFileWidgetExports, FirstToFileWidgetFeatureFlags, FirstToFileWidgetXProps, IPCase, OrientationType } from '@model/widget.model';
import { WidgetComponent } from '../ftf-widget/ftf-widget.component';
import { base64ToBlob, formatFileSize } from '@utils/file-utils';
import { Subject, takeUntil } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { MatDrawerContainer } from '@angular/material/sidenav';

export interface WidgetEvent {
  type: keyof FirstToFileWidgetXProps;
  message: string;
  timestamp: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detail?: any;
}

function falsyToUndefined(value: string | null | undefined): string | undefined {
  return value === '' || value == null ? undefined : value;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {

  form!: FormGroup;
  enableWidget = false;
  events: WidgetEvent[] = [];
  selectedCommandIndex = 0;
  orientationOptions: OrientationType[] = ['Auto', 'Vertical', 'Horizontal'];
  isSmallScreen = true;
  currentLayout: OrientationType = 'Auto';
  currentWidth = 320;
  isDragging = false;

  private destroy$ = new Subject<void>();

  @ViewChild('console') private consoleContainer!: ElementRef;
  @ViewChild(WidgetComponent) private widget!: WidgetComponent;

  constructor(
    private fb: FormBuilder,
    private zone: NgZone,
    private breakpointObserver: BreakpointObserver
  ) {
      this.breakpointObserver
        .observe([Breakpoints.Small, Breakpoints.XSmall])
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          // If the screen matches small or xsmall breakpoints, it's a small screen
          this.isSmallScreen = result.matches;
        });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      // ====== Properties Section
      productName: ['ACME'],
      orientation: [this.currentLayout],
      events: this.fb.group({
        onLoad: [true],
        onAuth: [true],
        onCaseOpen: [true],
        onCommand: [true],
        onDocumentAction: [true],
      }),
      featureFlags: this.fb.group({
        upload: [null],
        openInIpms: [null],
        workflowActions: [null],
        documentPreview: [null],
        documentDownload: [null],
        documentSendToAction: [null],
        documentChangeStatus: [null],
        patent: [null],
        trademark: [null],
        matter: [null],
      }),
      // future properties here ...

      // ====== Command Section
      openByAppSerNum: this.fb.group({
        type: ['All', Validators.required],
        appSerNum: ['', Validators.required],
      }),
      openByCaseRefNum: this.fb.group({
        type: ['All', Validators.required],
        caseRefNum: ['', Validators.required],
      }),
      download: this.fb.group({
        type: ['Patent', Validators.required],
        id: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
        name: ['', Validators.required],
      }),
    });
    this.toggleForm();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  ngAfterViewInit() {
    this.form.get('orientation')?.valueChanges.subscribe((value) => {
      this.currentLayout = value;
    });
  }

  onLoad() {
    this.addEventMessage('onLoad', '');
  }

  updateWidth(container: MatDrawerContainer, maxWidth = 320, percentage?: number): void {
    if (percentage) {
      this.currentWidth = Math.max(Math.round(window.innerWidth * percentage), maxWidth);
    } else {
      this.currentWidth = maxWidth; // Reset to default width
    }
    setTimeout(() => container.updateContentMargins(), 300);
  }

  onDragMoved(event: CdkDragMove<unknown>) {
    this.currentWidth = window.innerWidth - event.pointerPosition.x + 5;

    // Reset transform so the resizer stays positioned at the sidebar edge
    const element = event.source.element.nativeElement;
    element.style.transform = 'none';
  }

  onDragStarted() {
    this.isDragging = true;
  }

  onDragEnded(container: MatDrawerContainer) {
    setTimeout(() => {
      this.isDragging = false;
      container.updateContentMargins();
    }, 300);
  }

  onDocumentAction(event: DocumentAction) {
    const first = event.selection.trifold[0] || event.selection.reference[0];
    if (event.selection.reference.length > 0) {
      this.updateDownloadFormValues(first, 'Reference');
    } else if (event.selection.trifold.length > 0) {
      this.updateDownloadFormValues(first, event.case.type);
    }
    this.addEventMessage('onDocumentAction', `Case Ref Num: ${event.case.caseRefNum} (${event.case.type}) with #${event.selection.trifold.length + event.selection.reference.length} Doc(s)`, event);
  }

  onCaseOpen(event: IPCase) {
    this.addEventMessage('onCaseOpen', `Case Ref Num: ${event.caseRefNum} (${event.type})`, event);
  }

  onCommand(event: { command: keyof FirstToFileWidgetExports, args: [] }) {
    this.addEventMessage('onCommand', `${event.command} triggered on ${event.args.length > 0 ? '[' + event.args.join(', ') + ']' : 'no args'}`, event);
  }

  onAuth() {
    this.addEventMessage('onAuth', '');
  }

  toggleForm(): void {
    if (this.enableWidget) {
      this.form.enable();
      this.form.markAllAsTouched();
    } else {
      this.form.disable();
    }
  }

  addEventMessage(type: keyof FirstToFileWidgetXProps, message: string, detail?: unknown) {
    const timestamp = new Date().toLocaleTimeString();
    this.zone.run(() => this.events.push({ timestamp, type, message, detail }));
  }

  private scrollToBottom(): void {
    this.consoleContainer.nativeElement.scrollTop = this.consoleContainer.nativeElement.scrollHeight;
  }

  updateDownloadFormValues(doc: Document, type: string): void {
    if (!doc.downloadable) return;
    this.form.patchValue({
      download: { type, id: doc.id, name: doc.fileName },
    });
    this.selectedCommandIndex = 2;
  }

  openDocument() {
    const doc = this.form.get('download')?.getRawValue();
    this.widget.downloadDocument(doc.id, doc.name, doc.type).then((resp) => {
      const url = window.URL.createObjectURL(base64ToBlob(resp.base64));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name!;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    });
  }

  openByAppSerNum() {
    const rec = this.form.get('openByAppSerNum')?.getRawValue();
    this.widget?.openByAppSerNum(rec.appSerNum, rec.type === 'All' ? undefined : rec.type);
  }

  openByCaseRefNum() {
    const rec = this.form.get('openByCaseRefNum')?.getRawValue();
    this.widget?.openByCaseRefNum(rec.caseRefNum, rec.type === 'All' ? undefined : rec.type);
  }

  formatSize(size?: number) {
    return formatFileSize(size);
  }

  get widgetConfig(): string {
    const calls = [];
    let form = this.form.get('download');
    if (form?.valid) {
      const rec = form.getRawValue();
      const type = rec.type === 'All' ? '' : `, '${rec.type}'`;
      calls.push(`instance.downloadDocument(${rec.id}, '${rec.name}'${type}).then((resp) => {\n      console.log('File contents', resp.base64);\n    });`);
    }
    form = this.form.get('openByAppSerNum');
    if (form?.valid) {
      const rec = form.getRawValue();
      const type = rec.type === 'All' ? '' : `, '${rec.type}'`;
      calls.push(`instance.openByAppSerNum('${rec.appSerNum}'${type});`);
    }
    form = this.form.get('openByCaseRefNum');
    if (form?.valid) {
      const rec = form.getRawValue();
      const type = rec.type === 'All' ? '' : `, '${rec.type}'`;
      calls.push(`instance.openByCaseRefNum('${rec.caseRefNum}'${type});`);
    }

    // Dynamically include event handlers based on the form values
    const events = this.form.get('events')?.value;
    const eventHandlers = [];
    if (events?.onLoad) {
      eventHandlers.push(`onLoad: () => { console.log('Widget loaded'); }`);
    }
    if (events?.onAuth) {
      eventHandlers.push(`onAuth: () => { console.log('Authentication triggered'); }`);
    }
    if (events?.onCaseOpen) {
      eventHandlers.push(`onCaseOpen: (item) => { console.log('Case opened:', item); }`);
    }
    if (events?.onCommand) {
      eventHandlers.push(`onCommand: (command, args) => {\n        console.log('Command executed:', command, args);\n      }`);
    }
    if (events?.onDocumentAction) {
      eventHandlers.push(`onDocumentAction: (doc) => { console.log('Document action:', doc); }`);
    }
    const orientation = this.form.get('orientation')?.value ? `orientation: "${this.form.get('orientation')?.value || ''}",\n      ` : '';
    const flags: string[] = [];
    FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS.forEach((key) => {
      const value = this.form.get('featureFlags')?.get(key)?.value;
      if (value !== undefined && value !== null) {
        flags.push(`${key}: ${value}`);
      }
    });
    const featureFlags = flags.length > 0 ? `featureFlags: {\n        ${flags.join(',\n        ')}\n      },\n      ` : '';

    return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <title>${this.form.get('productName')?.value || ''}</title>
    <script src="${__APP_BASE_URL__}/zoid/zoid.js"
              type="text/javascript"></script>
    <script src="${__APP_BASE_URL__}/widget/widget.js"
              type="text/javascript"></script>
    <script type="text/javascript">
    const instance = FirstToFileWidget({
      productName: "${this.form.get('productName')?.value || ''}",
      ${orientation}${featureFlags}${eventHandlers.join(',\n      ')}
    });
    instance.render('#container');
    ${calls.join('\n    ')}
    </script>
  </head>
  <body>
    <div id="container"></div>
  </body>
</html>
`;
  }

  get showTabsLayout(): boolean {
    const orientation = this.form.get('orientation')?.value;
    return orientation === 'Vertical' || (orientation === 'Auto' && this.isSmallScreen);
  }

  get showPanelsLayout(): boolean {
    const orientation = this.form.get('orientation')?.value;
    return orientation === 'Horizontal' || (orientation === 'Auto' && !this.isSmallScreen);
  }

  get orientation() { return falsyToUndefined(this.form.get('orientation')?.value) as OrientationType | undefined; }

  get featureFlags() {
    let flagCount = 0;
    const flags = FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS.reduce((acc, key) => {
      const value = this.form.get('featureFlags')?.get(key)?.value;
      if (value === undefined || value === null) return acc;
        flagCount++;
        acc[key] = value;
        return acc;
    }, {} as FirstToFileWidgetFeatureFlags);
    // return undefined if all flags are null or undefined
    return flagCount > 0 ? flags : undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
